import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cloneCampaign,
  getCampaign,
  getTemplates,
  runGovernanceCheck,
  transitionCampaign,
} from "../api/client.js";
import { Shell } from "../components/Shell.jsx";
import {
  GovernanceChecklist,
  LifecycleStrip,
  Loading,
  ErrorCard,
  MetricValue,
  Panel,
  StatusPill,
  formatCount,
} from "../components/ui.jsx";
import { Link, useNavigate } from "react-router-dom";
import { DOMAIN_LABELS } from "../domainModel.js";
import { displayChannelLabel } from "../channelModel.js";
import { campaignLifecycleFor } from "../campaignLifecycleContract.js";
import { GOVERNANCE_ALLOWED_ACTIONS, governanceIssueFor } from "../governanceIssueContract.js";
import { ACTIVATION_PATHS, campaignBuilderRoute, monitoringRoute } from "../navConfig.js";
import { formatSendTime, sendTimingLabel, touchSendTime } from "../sendSchedule.js";

const ACTION_LABELS = {
  submit: "Submit for review",
  approve: "Approve campaign",
  reject: "Reject and return to draft",
  revise: "Return to draft",
  deploy: "Publish campaign",
  complete: "Mark completed",
  cancel: "Cancel campaign",
};
const PRIMARY_ACTIONS = new Set(["submit", "approve", "deploy", "complete"]);
const WORKFLOW_LABELS = {
  draft: "Draft",
  ready_for_review: "Ready for review",
  approved: "Approved",
  deployed: "Published",
  completed: "Completed",
  cancelled: "Cancelled",
};
const HISTORY_ACTION_LABELS = {
  create: "Campaign draft created",
  update: "Campaign setup updated",
  submit: "Submitted for review",
  approve: "Approved by an independent reviewer",
  reject: "Rejected and returned to draft",
  revise: "Returned to draft for changes",
  deploy: "Campaign definition published",
  complete: "Campaign marked completed",
  cancel: "Campaign cancelled",
  governance_check: "Automated governance checks re-run",
};
const CHECK_ORDER = [
  "quality_gate",
  "latest_run",
  "enrollment_alignment",
  "min_cohort",
  "guardrail_exclusion",
  "template_coverage",
  "message_content",
  "sensitivity_channel",
  "cadence_caps",
  "safety_disclaimer",
];
function canRunAction(auth, action) {
  const roles = new Set(auth?.roles || []);
  if (roles.has("tenant_admin")) return true;
  if (["approve", "reject"].includes(action)) return roles.has("campaign_reviewer");
  if (action === "deploy") return roles.has("campaign_operator");
  return roles.has("hr_manager");
}

function plainList(values, fallback = "All eligible values") {
  if (!values?.length) return fallback;
  return values.map((value) => String(value).replace(/_/g, " ")).join(", ");
}

function historyActor(event, campaign) {
  if (event.actor === "system") return "Automated service";
  if (event.actor === campaign.ownerId) return "Campaign owner";
  return "Authorized user";
}

function governanceStatus(value) {
  if (value === "pass") return { status: value, label: "Checks passed" };
  if (value === "warn") return { status: value, label: "Review warning" };
  if (value === "fail") return { status: value, label: "Blocked" };
  return { status: "neutral", label: "Not checked" };
}

export function CampaignDetail({ tenant, enums, campaignId, auth, params }) {
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const cameFromMonitoring = params?.get("from") === "monitoring";

  const load = useCallback(
    () =>
      getCampaign(campaignId)
        .then((data) => {
          setCampaign(data);
          return getTemplates(data.programDomain).then((templateData) =>
            setTemplates(templateData.templates)
          );
        })
        .catch(setError),
    [campaignId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const act = async (action) => {
    setBusy(true);
    setActionError(null);
    try {
      await transitionCampaign(campaignId, action);
      await load();
    } catch (err) {
      setActionError(err);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const clone = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const created = await cloneCampaign(campaignId);
      navigate(campaignBuilderRoute({ source: "campaigns", campaignId: created.id, step: "program" }));
    } catch (err) {
      setActionError(err);
      setBusy(false);
    }
  };

  const recheck = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await runGovernanceCheck(campaignId);
      await load();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  };

  const orderedGovernance = useMemo(() => {
    if (!campaign?.governance) return null;
    const rank = new Map(CHECK_ORDER.map((id, index) => [id, index]));
    return {
      ...campaign.governance,
      checks: [...campaign.governance.checks].sort(
        (left, right) => (rank.get(left.id) ?? 99) - (rank.get(right.id) ?? 99)
      ),
    };
  }, [campaign]);

  const body = () => {
    if (error) return <ErrorCard error={error} />;
    if (!campaign) return <Loading />;

    const governanceBlocked = campaign.governance?.overall === "fail";
    const transitions = Object.keys(enums.transitions[campaign.workflowStatus] || {});
    const actions = governanceBlocked
      ? []
      : transitions
          .filter((action) => canRunAction(auth, action))
          .filter((action) => !["approve", "reject"].includes(action) || auth?.userId !== campaign.ownerId);
    const awaitingReview = campaign.workflowStatus === "ready_for_review";
    // Day 0 = publication: once published, startsAt is the publication time.
    const publishedAt = campaign.deployedAt ? campaign.startsAt || campaign.deployedAt : null;
    const isCampaignOwner = auth?.userId === campaign.ownerId;
    const hasReviewerPermission = canRunAction(auth, "approve");
    const mayMakeReviewDecision =
      awaitingReview && !governanceBlocked && hasReviewerPermission && !isCampaignOwner;
    const failedChecks = campaign.governance?.checks.filter((check) => check.status === "fail") || [];
    const primaryIssue = governanceIssueFor(failedChecks[0]);
    const affectedStep = primaryIssue.affectedBuilderStep;
    const affectedStepLabel = primaryIssue.affectedBuilderStepLabel;
    const mayEdit =
      campaign.workflowStatus === "draft" || (transitions.includes("revise") && canRunAction(auth, "revise"));
    const stageLabels = (campaign.cohortFilters.stageIds || []).map(
      (id) =>
        enums.stages[campaign.programDomain]?.find((stage) => stage.id === Number(id))?.label || `Stage ${id}`
    );
    const templateName = (id) =>
      templates.find((template) => template.id === id)?.name || "Template unavailable";

    const editSetup = async () => {
      setBusy(true);
      setActionError(null);
      try {
        if (campaign.workflowStatus !== "draft") await transitionCampaign(campaignId, "revise");
        navigate(campaignBuilderRoute({ source: "campaigns", campaignId, step: affectedStep }));
      } catch (err) {
        setActionError(err);
        setBusy(false);
      }
    };

    return (
      <>
        {actionError && (
          <div className="gate-banner fail" role="alert">
            This action could not be completed: {actionError.detail || actionError.message}
          </div>
        )}

        <LifecycleStrip items={campaignLifecycleFor(campaign)} />

        {governanceBlocked && (
          <section className="campaign-blocker" role="alert" aria-labelledby="publication-blocked-title">
            <div className="campaign-blocker-heading">
              <div>
                <span className="campaign-blocker-eyebrow">Publication blocker</span>
                <h2 id="publication-blocked-title">Publication is blocked</h2>
              </div>
              <StatusPill status="blocked" label="Action required" />
            </div>
            <p>
              The campaign cannot be approved or published until the failed governance checks are resolved and
              re-evaluated.
            </p>
            <div className="campaign-blocker-list">
              {failedChecks.map((check) => {
                const guidance = governanceIssueFor(check);
                return (
                  <article key={check.id}>
                    <h3>{check.label}</h3>
                    <p>{check.detail}</p>
                    <dl>
                      <div>
                        <dt>Owner</dt>
                        <dd>{guidance.owner}</dd>
                      </div>
                      <div>
                        <dt>Resolution</dt>
                        <dd>{guidance.resolution}</dd>
                      </div>
                    </dl>
                  </article>
                );
              })}
            </div>
            <div className="campaign-blocker-action">
              <span className="campaign-blocker-action-label">Allowed next action</span>
              {mayEdit && primaryIssue.allowedAction === GOVERNANCE_ALLOWED_ACTIONS.EDIT_CAMPAIGN_SETUP ? (
                <>
                  <button className="bi-btn-primary" type="button" disabled={busy} onClick={editSetup}>
                    Edit campaign setup
                  </button>
                  <small>
                    Opens {affectedStepLabel} for this campaign. Other decisions remain unavailable until the
                    campaign is corrected and resubmitted for governance review.
                  </small>
                </>
              ) : (
                <p className="campaign-blocker-next">
                  No correction action is available to your role. An authorized campaign owner must update
                  {` ${affectedStepLabel}`}.
                </p>
              )}
            </div>
          </section>
        )}

        <div className={`campaign-summary-grid${governanceBlocked ? " single" : ""}`}>
          <Panel title="Campaign summary" desc="The aggregate definition currently under lifecycle control">
            <dl className="campaign-detail-list">
              <div>
                <dt>Workflow</dt>
                <dd>
                  <StatusPill status={campaign.workflowStatus} />
                </dd>
              </div>
              <div>
                <dt>Program</dt>
                <dd>{DOMAIN_LABELS[campaign.programDomain] || campaign.programDomain}</dd>
              </div>
              <div>
                <dt>Campaign goal</dt>
                <dd>
                  {campaign.activationGoal === "goal1_enrollment"
                    ? "Enroll eligible people"
                    : "Increase active program usage"}
                </dd>
              </div>
              <div>
                <dt>Evidence period</dt>
                <dd>{campaign.runMonth || "Unavailable"}</dd>
              </div>
              <div>
                <dt>Aggregate audience</dt>
                <dd>
                  <MetricValue
                    value={campaign.cohortEstimate?.memberCount}
                    grain="covered people"
                    suppressed={campaign.cohortEstimate?.meetsMinimum === false}
                    minimum={enums.minCohortSize}
                  />
                </dd>
              </div>
              <div>
                <dt>Start</dt>
                <dd>{publishedAt ? formatSendTime(touchSendTime(publishedAt, 0)) : "When the campaign is published"}</dd>
              </div>
              <div>
                <dt>Version</dt>
                <dd>{campaign.version || 1}</dd>
              </div>
              <div>
                <dt>Accountable role</dt>
                <dd>Campaign owner</dd>
              </div>
            </dl>
          </Panel>

          {!governanceBlocked && (
            <Panel
              title="Available decisions"
              desc="Actions shown here are limited by your role and the current lifecycle state"
            >
              <div className="campaign-actions">
                {actions.length === 0 && (
                  <span className="campaign-no-actions">
                    No workflow decisions are available in this state.
                  </span>
                )}
                {actions.map((action) => (
                  <button
                    key={action}
                    type="button"
                    disabled={busy}
                    className={PRIMARY_ACTIONS.has(action) ? "bi-btn-primary" : "bi-btn-ghost"}
                    onClick={() => act(action)}
                  >
                    {ACTION_LABELS[action] || action}
                  </button>
                ))}
                {campaign.workflowStatus === "draft" && (
                  <Link
                    className="bi-btn-ghost"
                    to={campaignBuilderRoute({ source: "campaigns", campaignId, step: "program" })}
                  >
                    Continue campaign setup
                  </Link>
                )}
                <button className="bi-btn-ghost" type="button" disabled={busy} onClick={clone}>
                  Clone to new draft
                </button>
              </div>
              <p className="campaign-action-note">
                Approval records an independent decision. Publication only makes the approved definition
                available to authorized downstream communications processing.
              </p>
              {awaitingReview && (
                <div
                  className={`review-authority-state${mayMakeReviewDecision ? " authorized" : " restricted"}`}
                  role="status"
                >
                  <StatusPill
                    status={mayMakeReviewDecision ? "available" : "awaiting_review"}
                    label={mayMakeReviewDecision ? "Decision available" : "Independent reviewer required"}
                  />
                  <div>
                    <strong>
                      {mayMakeReviewDecision
                        ? "You are authorized to review this version"
                        : isCampaignOwner
                          ? "The campaign owner cannot approve or reject this version"
                          : "Approval and rejection require campaign reviewer permission"}
                    </strong>
                    <span>
                      {mayMakeReviewDecision
                        ? "Approve or reject only after completing the evidence, privacy, and content-safety review."
                        : "A different authorized reviewer must record the independent decision."}
                    </span>
                  </div>
                </div>
              )}
            </Panel>
          )}
        </div>

        {campaign.contactGovernance && (
          <div className="gate-banner warn campaign-contact-summary">
            Contact policy evaluated {formatCount(campaign.contactGovernance.candidateMembers)} covered people
            across {formatCount(campaign.contactGovernance.plannedTouches)} planned touches. It reserved{" "}
            {formatCount(campaign.contactGovernance.reservedTouches)} touches and held{" "}
            {formatCount(campaign.contactGovernance.heldTouches)} to prevent cross-campaign over-contact.
          </div>
        )}

        <div className="bi-panels campaign-governance-grid">
          <Panel
            title="Governance review"
            desc="Automated results support the reviewer; they never grant approval"
            headerRight={
              <div className="campaign-panel-actions">
                {campaign.governance && <StatusPill {...governanceStatus(campaign.governance.overall)} />}
                {!governanceBlocked && (
                  <button className="bi-btn-ghost" type="button" disabled={busy} onClick={recheck}>
                    Re-run checks
                  </button>
                )}
              </div>
            }
          >
            <ol className="governance-review-order">
              <li>
                <strong>1. Evidence integrity</strong>
                <span>
                  Validate source quality, evidence period, and alignment between the goal and audience.
                </span>
              </li>
              <li>
                <strong>2. Privacy protection</strong>
                <span>Validate minimum audience size, guardrail exclusions, and aggregate-only use.</span>
              </li>
              <li>
                <strong>3. Content safety</strong>
                <span>
                  Validate template coverage, message content, channel sensitivity, cadence, and disclaimers.
                </span>
              </li>
              <li>
                <strong>4. Maker-checker decision</strong>
                <span>
                  An authorized reviewer other than the campaign owner may approve or reject this version.
                </span>
              </li>
            </ol>
            <GovernanceChecklist result={orderedGovernance} />
          </Panel>

          <Panel
            title="Audience definition"
            desc="Aggregate cohort rules; no person-level records are displayed"
          >
            <dl className="campaign-detail-list compact">
              <div>
                <dt>Clinical stages</dt>
                <dd>{plainList(stageLabels)}</dd>
              </div>
              <div>
                <dt>Priority bands</dt>
                <dd>{plainList(campaign.cohortFilters.bands)}</dd>
              </div>
              <div>
                <dt>Condition groups</dt>
                <dd>{plainList(campaign.cohortFilters.subdomains)}</dd>
              </div>
              <div>
                <dt>Recommended actions</dt>
                <dd>{plainList(campaign.cohortFilters.recommendedActions)}</dd>
              </div>
              <div>
                <dt>Confidence</dt>
                <dd>{plainList(campaign.cohortFilters.confidenceLevels)}</dd>
              </div>
              <div>
                <dt>Sensitivity</dt>
                <dd>{campaign.sensitivityTier === "high" ? "High — private channels only" : "Standard"}</dd>
              </div>
            </dl>
          </Panel>
        </div>

        <Panel
          full
          title="Workday app sequence"
          desc={`${campaign.channelPlan.length} planned ${campaign.channelPlan.length === 1 ? "touch" : "touches"} over ${Math.max(...campaign.channelPlan.map((touch) => touch.sequenceDay), 0)} days`}
        >
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Campaign Workday app sequence">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>Timing</th>
                  <th>Delivery</th>
                  <th>Starting template</th>
                  <th>Campaign text</th>
                </tr>
              </thead>
              <tbody>
                {campaign.channelPlan.map((touch, index) => {
                  const template = templates.find((item) => item.id === touch.templateId) || {};
                  return (
                    <tr key={`${touch.channel}-${touch.sequenceDay}-${index}`}>
                      <td>
                        {sendTimingLabel(touch.sequenceDay)}
                        {publishedAt && (
                          <>
                            <br />
                            <small>{formatSendTime(touchSendTime(publishedAt, touch.sequenceDay))}</small>
                          </>
                        )}
                      </td>
                      <td>{displayChannelLabel(touch.channel, enums)}</td>
                      <td>{templateName(touch.templateId)}</td>
                      <td>
                        <CampaignMessageText touch={touch} template={template} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>

        <div className="campaign-audit-section">
          <Panel
            full
            title="Decision and audit history"
            desc="Plain-language record of changes and lifecycle decisions"
          >
            {campaign.history.length === 0 && <p className="campaign-no-actions">No history is available.</p>}
            {campaign.history.map((event, index) => (
              <div key={`${event.at}-${index}`} className="history-item">
                <span className="when">
                  {event.at ? new Date(event.at).toLocaleString() : "Time unavailable"}
                </span>
                <span>
                  <strong>{HISTORY_ACTION_LABELS[event.action] || "Campaign record updated"}</strong>
                  {event.from
                    ? ` — ${WORKFLOW_LABELS[event.from] || "Previous state"} to ${WORKFLOW_LABELS[event.to] || "New state"}`
                    : ""}
                  {` by ${historyActor(event, campaign)}`}
                  {event.note ? ` — ${event.note}` : ""}
                </span>
              </div>
            ))}
          </Panel>
        </div>
      </>
    );
  };

  return (
    <Shell
      tenant={tenant}
      auth={auth}
      activeNav="campaigns"
      title={campaign?.name || "Campaign"}
      subtitle={
        campaign
          ? `${DOMAIN_LABELS[campaign.programDomain] || campaign.programDomain} · evidence period ${campaign.runMonth || "unavailable"}`
          : ""
      }
      headerRight={
        <button
          className="bi-btn-ghost"
          type="button"
          onClick={() =>
            navigate(
              cameFromMonitoring
                ? monitoringRoute({ programDomain: params?.get("programDomain") || campaign?.programDomain })
                : ACTIVATION_PATHS.campaigns
            )
          }
        >
          {cameFromMonitoring ? "Back to monitoring" : "Campaigns"}
        </button>
      }
    >
      {body()}
    </Shell>
  );
}

function campaignValue(touch, field, fallback = "") {
  return Object.prototype.hasOwnProperty.call(touch, field) ? touch[field] || "" : fallback || "";
}

function CampaignMessageText({ touch, template }) {
  const subject = campaignValue(touch, "subject", template.subject);
  const body = campaignValue(touch, "body", template.body);
  const workdayMessage = campaignValue(touch, "pushMessage", template.pushMessage || template.body);
  const disclaimer = campaignValue(touch, "safetyDisclaimer", template.safetyDisclaimer);

  return (
    <div className="campaign-message-copy">
      {(touch.channel === "email" || touch.channel === "both") && (
        <>
          <strong>Email subject</strong>
          <div>{subject || "Not provided"}</div>
          <strong>Email body</strong>
          <div>{body || "Not provided"}</div>
        </>
      )}
      {(touch.channel === "sms" || touch.channel === "workday" || touch.channel === "both") && (
        <>
          <strong>Workday app message</strong>
          <div>{workdayMessage || "Not provided"}</div>
        </>
      )}
      <strong>Safety disclaimer</strong>
      <div>{disclaimer || "Not provided"}</div>
    </div>
  );
}
